import toLower from 'lodash/toLower';
import trim from 'lodash/trim';
import isEmpty from 'lodash/isEmpty';
import type { State } from '../types';
import type { CartProductSchema } from '../../types';

function matches(source: string, target = '') {
  return toLower(source).includes(toLower(target));
}

function hasFilterStatus(
  sourceStatuses: CartProductSchema['productStatuses'],
  target: CartProductSchema['productStatuses']
) {
  return !!target?.find((status) => sourceStatuses?.includes(status));
}

function getProductsMatchingSearchQuery({
  products,
  offerings,
  searchText,
}: {
  products: CartProductSchema[];
  offerings: State['offeringsAndpricingPlans'];
  searchText?: string;
}) {
  return products.filter((product) => {
    const { entitlementNumber, cloudSite, name, offeringId, productId } = product;
    const offeringsForProduct = offerings?.[productId] ?? [];
    const selectedOffering = offeringsForProduct.find(({ id }) => id === offeringId);
    const offeringName = selectedOffering?.offeringName ?? '';

    const orgName = product?.organisationName ?? '';

    return (
      matches(entitlementNumber, searchText) ||
      matches(cloudSite, searchText) ||
      matches(name, searchText) ||
      matches(offeringName, searchText) ||
      matches(orgName, searchText)
    );
  });
}

function getProductsMatchingFilterStatuses({
  products,
  filterStatuses,
}: {
  products: CartProductSchema[];
  filterStatuses: CartProductSchema['productStatuses'];
}) {
  return products.filter((product) => {
    const { productStatuses } = product;

    return hasFilterStatus(productStatuses, filterStatuses);
  });
}

export function getSearchResults({
  selectedProducts,
  offerings,
  searchText,
  filterStatuses,
}: {
  selectedProducts: CartProductSchema[];
  offerings: State['offeringsAndpricingPlans'];
  searchText?: string;
  filterStatuses: CartProductSchema['productStatuses'];
}): CartProductSchema[] {
  const productsMatchingSearchQuery = getProductsMatchingSearchQuery({
    products: selectedProducts,
    offerings,
    searchText,
  });

  if (!filterStatuses?.length) return productsMatchingSearchQuery;

  const productsMatchingSearchQueryAndFilterStatuses = getProductsMatchingFilterStatuses({
    products: productsMatchingSearchQuery,
    filterStatuses,
  });

  return productsMatchingSearchQueryAndFilterStatuses;
}
